import React from 'react';
import PropTypes from 'prop-types';
import LoadingCard from '../../../loadingCard';
import OrganizationModel from '../../../../models/organization';
import { TopBar, Dropdown, CopyToClipboardLink, Icon } from '@weezevent/nacre';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { usePermissions } from '../../../../user/permissions';

import Style from '../topBarModules.module.css';

function ItemLine({ element }) {
    const { t } = useTranslation();

    return (
        <>
            {element && (
                <div className={Style['switch-organization-item-subtitle']}>
                    <div className={Style['switch-orga-stop-propagation']}>
                        <CopyToClipboardLink copyOnIconClick delay={1000} tooltipMini value={element} tooltip={t('ticket.common.action.copy')}>
                            {element}
                        </CopyToClipboardLink>
                    </div>
                </div>
            )}
        </>
    );
}

function SwitchOrganizationItem({ organization }) {
    const history = useHistory();
    const permissions = usePermissions();
    const isAdmin = permissions.hasCapacities(['PyvarCapacity.ADMIN', 'PyvarCapacity.ORGANIZATION'], organization.id);

    const title = (
        <div className={Style['switch-organization-item-title-container']}>
            <div>
                <Icon className={Style['switch-organization-item-icon']} name="user" margins={false} />
            </div>
            <div className={Style['switch-organization-item-infos']}>
                <div className={Style['switch-organization-item-title']}>O{`${organization.id} ${organization.name ? '•' : ''} ${organization.name}`}</div>
                {isAdmin && (
                    <>
                        <ItemLine element={organization.email} />
                        <ItemLine element={organization.phone_number} />
                    </>
                )}
            </div>
        </div>
    );

    return (
        <Dropdown.Item
            key={organization.id}
            onClick={() => {
                history.push(`/ticket/O${organization.id}/events`);
            }}
        >
            {title}
        </Dropdown.Item>
    );
}

export default function Organization({ organization = null }) {
    const { t } = useTranslation();

    const [multiOrga, setMultiOrga] = React.useState(null);
    const [search, setSearch] = React.useState(null);

    const [organizations, loadingOrga, sync] = OrganizationModel.useApiModel(
        {
            // We have a fallback ui if this request fails
            strict: false,
            query: {
                limit: 10
            },
            onSuccess: orgas => {
                multiOrga === null && setMultiOrga(orgas.length > 1);
            }
        },
        [multiOrga]
    );

    const total = React.useMemo(() => {
        return organizations?.__meta.headers.get('total-count');
    }, [organizations]);

    const onChange = React.useCallback(
        (synth, search) => {
            let _queryParams = search ? { search } : null;
            setSearch(search || null);
            sync({ query: _queryParams, allow_cache: false, cache: false });
        },
        [sync]
    );

    const onReset = React.useCallback(() => {
        setSearch(null);
        sync({ query: { search: '', limit: 10 } });
    }, [sync]);

    const onFooterClick = React.useCallback(() => {
        const url = window.location.origin;
        // if we re on local or uat redirect straight to .be cause the page 'https://admin.weezevent.be/organizations' exists only en prod and staging
        if (url.startsWith('http://localhost') || url.startsWith('https://admin.uat')) {
            window.location.replace('https://admin.weezevent.be/organizations');
        } else {
            window.location.replace(`${url}/organizations`);
        }
    }, [organization]);

    const _name = React.useMemo(() => {
        if (!organization) {
            return null;
        }
        if (!organization.name || organization.name === '') {
            return `${organization.first_name} ${organization.last_name}`;
        }
        return organization.name;
    }, [organization]);

    if (!organization) {
        return null;
    }

    return (
        <TopBar.Organization
            id={organization.pk}
            name={_name}
            isSwitchOrga={Boolean(multiOrga)}
            searchProps={{
                value: search,
                onChange: onChange,
                handleReset: onReset,
                placeholder: t('ticket.switchOrga.search_placeholder')
            }}
            headerText={search && t('ticket.switchEvent.header_with_search')}
            footerText={total > 10 && t('ticket.switchOrga.footer_cta')}
            onFooterClick={total > 10 ? onFooterClick : undefined}
        >
            <LoadingCard loading={loadingOrga} height={'200px'}>
                {organizations?.length ? (
                    organizations.map(orga => <SwitchOrganizationItem key={orga.id} organization={orga} />)
                ) : (
                    <div className={Style['switch-organization__no-result']}>{t('ticket.switchOrga.no_results')}</div>
                )}
            </LoadingCard>
        </TopBar.Organization>
    );
}

Organization.propTypes = {
    organization: PropTypes.object
};
